window.selectedTags = window.selectedTags || [];

function getOwnTags(el) {
	const raw = el.getAttribute('data-tags');
	if (!raw) return [];
	return raw.split(';').map(t => t.trim()).filter(Boolean);
}

// Own tags + every descendant [data-tags] element's tags (e.g. a .project's bullets).
// querySelectorAll already returns all descendants, not just direct children, so this
// works the same whether the element carries its own data-tags (.project) or not (.job).
function collectAllTags(el) {
	const tags = new Set(getOwnTags(el));
	el.querySelectorAll('[data-tags]').forEach(child => {
		getOwnTags(child).forEach(t => tags.add(t));
	});
	return tags;
}

// Inverse-document-frequency index: how many [data-tags] elements (bullets,
// projects, and now skill list items) carry each tag, plus the total element
// count. Built once per page load since tags are static after render.
function computeTagIndex() {
	const freq = new Map();
	const elements = document.querySelectorAll('[data-tags]');
	elements.forEach(el => {
		getOwnTags(el).forEach(t => {
			freq.set(t, (freq.get(t) || 0) + 1);
		});
	});
	return { freq, total: elements.length };
}

window.tagIndex = window.tagIndex || computeTagIndex();

// Rare tags (matched by few elements) score higher than common ones, so
// matching one distinctive tag can outweigh matching several generic ones.
function tagWeight(tag) {
	const df = window.tagIndex.freq.get(tag) || 1;
	return Math.log(window.tagIndex.total / df);
}

function scoreElement(el) {
	// A project linked via ?project=<id> always ranks first (see resumeProjectFromUrl).
	if (window.pinnedProject && el.id === window.pinnedProject) return Infinity;
	if (!window.selectedTags.length) return 0;
	const tagSet = collectAllTags(el);
	let score = 0;
	window.selectedTags.forEach(t => {
		if (tagSet.has(t)) score += tagWeight(t);
	});
	return score;
}

function recalculateScores() {
	document.querySelectorAll('[data-tags]').forEach(el => {
		el.setAttribute('data-score', scoreElement(el));
	});
}

function isTagItemSelected(el) {
	return getOwnTags(el).some(t => window.selectedTags.includes(t));
}

// Reorders direct-child [data-tags] siblings under every parent that has more than one.
//
// Skill tag-items (.tags lists) sort selected-first, then by their fixed data-index —
// not by score — so an unselected tag always returns to its original spot instead of
// wherever it last happened to land in the DOM (which drifts across selections, since
// a plain stable sort ties-break on *current* DOM order, not original order).
//
// Everything else (bullets, projects) keeps score-based sorting, highest first, with
// ties falling back to their current relative order (stable sort).
function reorderSiblings() {
	const parents = new Set();
	document.querySelectorAll('[data-tags]').forEach(el => {
		if (el.parentElement) parents.add(el.parentElement);
	});

	parents.forEach(parent => {
		const children = Array.from(parent.children).filter(child =>
			child.hasAttribute('data-tags')
		);
		if (children.length < 2) return;

		const isTagList = children[0].classList.contains('tag-item');

		children
			.sort((a, b) => {
				if (isTagList) {
					const selectedA = isTagItemSelected(a) ? 1 : 0;
					const selectedB = isTagItemSelected(b) ? 1 : 0;
					if (selectedA !== selectedB) return selectedB - selectedA;
					const indexA = parseInt(a.getAttribute('data-index'), 10) || 0;
					const indexB = parseInt(b.getAttribute('data-index'), 10) || 0;
					return indexA - indexB;
				}
				const scoreA = parseFloat(a.getAttribute('data-score')) || 0;
				const scoreB = parseFloat(b.getAttribute('data-score')) || 0;
				return scoreB - scoreA;
			})
			// appendChild on an existing node moves it, so re-appending in sorted
			// order is enough to produce the final order without a manual removal step.
			.forEach(child => parent.appendChild(child));
	});
}

// Skill label -> discipline ("Front End" | "Back End" | "Server Management"),
// built from the data-discipline attribute print_skill_section() emits on
// skill <li> items. Skills with no discipline (e.g. tooling-practices) are
// simply absent and never affect the summary choice below.
function computeDisciplineLookup() {
	const lookup = new Map();
	document.querySelectorAll('.skill [data-discipline]').forEach(el => {
		getOwnTags(el).forEach(t => lookup.set(t, el.getAttribute('data-discipline')));
	});
	return lookup;
}

window.disciplineLookup = window.disciplineLookup || computeDisciplineLookup();

// A discipline only "wins" the summary when it has a clear lead: at least
// SUMMARY_MIN_LEADER selected skills in that discipline, and at least
// SUMMARY_MARGIN more than the next closest discipline. Anything short of
// that (nothing selected, a near-tie, or a spread across disciplines) falls
// back to the General summary rather than guessing.
const SUMMARY_MIN_LEADER = 2;
const SUMMARY_MARGIN = 2;

function selectSummaryId() {
	const counts = { 'Front End': 0, 'Back End': 0, 'Server Management': 0 };
	window.selectedTags.forEach(t => {
		const discipline = window.disciplineLookup.get(t);
		if (discipline && Object.prototype.hasOwnProperty.call(counts, discipline)) {
			counts[discipline]++;
		}
	});

	const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
	const [leaderId, leaderCount] = ranked[0];
	const runnerUpCount = ranked[1][1];

	if (leaderCount >= SUMMARY_MIN_LEADER && (leaderCount - runnerUpCount) >= SUMMARY_MARGIN) {
		return leaderId;
	}
	return 'General';
}

function updateSummary() {
	const activeId = selectSummaryId();
	document.querySelectorAll('.summary-variant').forEach(el => {
		el.classList.toggle('active', el.getAttribute('data-summary-id') === activeId);
	});
}

function updateTagResults() {
	recalculateScores();
	reorderSiblings();
	applyLayoutOps();
	applyJobBullets();
	updateSummary();
	document.body.classList.toggle('has-selected-tags', window.selectedTags.length > 0);
}

function toggleTag(tagEl) {
	const tagText = tagEl.textContent.trim();
	const i = window.selectedTags.indexOf(tagText);
	if (i > -1) {
		window.selectedTags.splice(i, 1);
	} else {
		window.selectedTags.push(tagText);
	}
	tagEl.classList.toggle('selected', window.selectedTags.includes(tagText));
	tagEl.setAttribute('aria-pressed', window.selectedTags.includes(tagText));
}

// Console helper: window.tailor('Angular', 'PHP', 'Data Architecture') selects exactly
// those skill tags (deselecting anything else currently selected) so a resume can be
// tailored for a specific application without manually clicking each tag. Unrecognized
// names are reported and otherwise ignored; calling tailor() with no arguments clears
// the selection entirely. Starting a new tailor() also clears any manual moves made with
// moveProject()/moveBullet() and friends (below), so each tailoring starts from scratch.
window.tailor = function (...tags) {
	window.layoutOps = [];
	window.jobBulletCounts = [];
	const tagButtons = Array.from(document.querySelectorAll('.skill .tag'));
	const matched = [];
	const missing = [];

	tags.forEach(tag => {
		const btn = tagButtons.find(el => el.textContent.trim() === tag);
		if (btn) {
			matched.push(btn);
		} else {
			missing.push(tag);
		}
	});

	tagButtons.forEach(btn => {
		const tagText = btn.textContent.trim();
		const shouldBeSelected = matched.includes(btn);
		const isSelected = window.selectedTags.includes(tagText);
		if (shouldBeSelected !== isSelected) {
			toggleTag(btn);
		}
	});

	updateTagResults();

	if (missing.length) {
		console.warn('tailor(): no matching skill tag(s) for:', missing.join(', '));
	}
	console.log('tailor(): selected', window.selectedTags.join(', ') || '(none)');
};

// ---------------------------------------------------------------------------
// Manual layout helpers, for adjusting what tailor() produced by hand:
//   layout([filter])              what's on the page; layout('apti') lists every bullet of one job/project
//   moveProject(match, position)  put a project at a position (1-4 are shown)
//   swapProjects(a, b)            exchange two projects' positions
//   moveBullet(match, position)   put a bullet at a position in its job/project (job: 1-3 show, 1-2 for the last job; project: 1-3, 1-2 for the fourth)
//   swapBullets(inMatch, outMatch) inMatch takes outMatch's slot (so hidden-in, visible-out)
//   jobBullets(apti, sandstorm, artistic)  how many bullets each job shows (default 3, 3, 2); pass null to keep a job's default
//   recipe()                      the whole setup (tags + moves) as paste-ready code
//   resetLayout()                 drop the manual moves, back to score order
// `match` is a project id/title/URL fragment, or a distinctive piece of a bullet's text
// (case-insensitive). Every move is stored as an absolute position and replayed after each
// re-sort, so clicking skill tags doesn't undo it. Only rendered items can be moved: a
// project needs "resume": {"include": true} in its JSON to be on the page at all.
// ---------------------------------------------------------------------------
window.layoutOps = window.layoutOps || [];

function layoutText(el) {
	return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

function layoutProjectTitle(project) {
	const title = project.querySelector('.item-title .title');
	return title ? layoutText(title).replace(/\s*→\s*$/, '') : project.id;
}

function layoutSiblings(el) {
	const selector = el.classList.contains('project') ? '.project' : '.highlight';
	return Array.from(el.parentElement.children).filter(child => child.matches(selector));
}

// 1-based position among same-kind siblings.
function layoutPosition(el) {
	return layoutSiblings(el).indexOf(el) + 1;
}

// Positions are clamped: past the end means last.
function layoutPlace(el, position) {
	const others = layoutSiblings(el).filter(child => child !== el);
	const index = Math.max(0, Math.min(position - 1, others.length));
	if (index < others.length) {
		el.parentElement.insertBefore(el, others[index]);
	} else if (others.length) {
		others[others.length - 1].after(el);
	}
}

function layoutMove(el, position) {
	layoutPlace(el, position);
	window.layoutOps.push({ el: el, position: layoutPosition(el) });
}

function applyLayoutOps() {
	window.layoutOps.forEach(op => {
		if (op.el.isConnected) layoutPlace(op.el, op.position);
	});
}

function layoutOwner(bullet) {
	const job = bullet.closest('.job');
	if (job) {
		const company = job.querySelector('.company');
		return company ? layoutText(company) : 'job';
	}
	const project = bullet.closest('.project');
	return project ? layoutProjectTitle(project) : '';
}

function findProject(match) {
	const query = String(match).trim().toLowerCase();
	const projects = Array.from(document.querySelectorAll('.projects .project'));
	const fields = project => {
		const link = project.querySelector('a.title');
		return [project.id, layoutProjectTitle(project), link ? link.getAttribute('href') : '']
			.map(field => field.toLowerCase());
	};

	let hits = projects.filter(project => project.id.toLowerCase() === query);
	if (!hits.length) hits = projects.filter(project => fields(project).some(field => field.includes(query)));
	if (hits.length === 1) return hits[0];

	console.warn(
		hits.length
			? 'No unique project for "' + match + '". Matches: ' + hits.map(project => project.id).join(', ')
			: 'No project matches "' + match + '". Projects on the page: ' + projects.map(project => project.id).join(', ')
	);
	return null;
}

function findBullet(match) {
	const query = layoutText({ textContent: String(match) }).toLowerCase();
	const bullets = Array.from(document.querySelectorAll('.highlight'));
	const hits = bullets.filter(bullet => layoutText(bullet).toLowerCase().includes(query));
	if (hits.length === 1) return hits[0];

	if (!hits.length) {
		console.warn('No bullet contains "' + match + '".');
	} else {
		console.warn('"' + match + '" matches ' + hits.length + ' bullets; use a longer fragment:');
		hits.forEach(bullet => console.warn('  [' + layoutOwner(bullet) + '] ' + layoutText(bullet).slice(0, 90)));
	}
	return null;
}

// Shortest run of a bullet's opening words that no other bullet contains.
function uniqueFragment(bullet) {
	const text = layoutText(bullet);
	const words = text.split(' ');
	const all = Array.from(document.querySelectorAll('.highlight')).map(el => layoutText(el).toLowerCase());
	for (let n = 3; n <= words.length; n++) {
		const fragment = words.slice(0, n).join(' ');
		const lower = fragment.toLowerCase();
		if (all.filter(other => other.includes(lower)).length === 1) return fragment;
	}
	return text;
}

function validPosition(name, position) {
	if (Number.isInteger(position) && position >= 1) return true;
	console.warn(name + '(): position must be a whole number, 1 or higher.');
	return false;
}

window.moveProject = function (match, position) {
	const project = findProject(match);
	if (!project || !validPosition('moveProject', position)) return;
	layoutMove(project, position);
	console.log('moveProject(): ' + layoutProjectTitle(project) + ' is now project ' + layoutPosition(project));
};

window.swapProjects = function (a, b) {
	const first = findProject(a);
	const second = findProject(b);
	if (!first || !second) return;
	if (first === second) {
		console.warn('swapProjects(): both names match the same project.');
		return;
	}
	const firstPosition = layoutPosition(first);
	const secondPosition = layoutPosition(second);
	layoutMove(first, secondPosition);
	layoutMove(second, firstPosition);
	console.log('swapProjects(): ' + layoutProjectTitle(first) + ' is now project ' + layoutPosition(first) +
		', ' + layoutProjectTitle(second) + ' is now project ' + layoutPosition(second));
};

window.moveBullet = function (match, position) {
	const bullet = findBullet(match);
	if (!bullet || !validPosition('moveBullet', position)) return;
	layoutMove(bullet, position);
	console.log('moveBullet(): [' + layoutOwner(bullet) + '] bullet is now #' + layoutPosition(bullet));
};

// inMatch takes outMatch's slot and outMatch takes inMatch's. Both bullets must be in the
// same job or project.
window.swapBullets = function (inMatch, outMatch) {
	const bulletIn = findBullet(inMatch);
	const bulletOut = findBullet(outMatch);
	if (!bulletIn || !bulletOut) return;
	if (bulletIn === bulletOut) {
		console.warn('swapBullets(): both fragments match the same bullet.');
		return;
	}
	if (bulletIn.parentElement !== bulletOut.parentElement) {
		console.warn('swapBullets(): those bullets are in different lists ([' + layoutOwner(bulletIn) + '] and [' +
			layoutOwner(bulletOut) + ']). Bullets can only be swapped within one job or project.');
		return;
	}
	const inPosition = layoutPosition(bulletIn);
	const outPosition = layoutPosition(bulletOut);
	layoutMove(bulletIn, outPosition);
	layoutMove(bulletOut, inPosition);
	console.log('swapBullets(): [' + layoutOwner(bulletIn) + '] "' + layoutText(bulletIn).slice(0, 50) + '…" is now #' +
		layoutPosition(bulletIn) + '; "' + layoutText(bulletOut).slice(0, 50) + '…" is now #' + layoutPosition(bulletOut));
};

// How many bullets each job shows, in page order (default 3, 3, 2). The CSS caps do the
// default work; this forces bullets on or off per application via .show-bullet/.hide-bullet.
window.jobBulletCounts = window.jobBulletCounts || [];

function applyJobBullets() {
	const jobs = Array.from(document.querySelectorAll('.experience .job')).slice(0, 3);
	jobs.forEach((job, index) => {
		const count = window.jobBulletCounts[index];
		Array.from(job.querySelectorAll('.highlight')).forEach((bullet, i) => {
			const forced = Number.isInteger(count);
			bullet.classList.toggle('show-bullet', forced && i < count);
			bullet.classList.toggle('hide-bullet', forced && i >= count);
		});
	});
}

window.jobBullets = function (...counts) {
	const valid = counts.every(count => count == null || (Number.isInteger(count) && count >= 0));
	if (!valid) {
		console.warn('jobBullets(): each count must be a whole number (0 or more), or null for the default.');
		return;
	}
	window.jobBulletCounts = counts;
	applyJobBullets();
	console.log('jobBullets(): bullets per job (Apti, Sandstorm, Artistic) = ' +
		[0, 1, 2].map(i => (Number.isInteger(counts[i]) ? counts[i] : 'default')).join(', '));
};

window.layout = function (filter) {
	const shown = el => window.getComputedStyle(el).display !== 'none';
	const clip = (text, length) => (text.length > length ? text.slice(0, length - 1) + '…' : text);
	const query = filter ? String(filter).trim().toLowerCase() : '';
	const score = el => (window.selectedTags.length ? ' (' + (parseFloat(el.getAttribute('data-score')) || 0).toFixed(1) + ')' : '');
	const lines = [];

	const projects = Array.from(document.querySelectorAll('.projects .project'));
	lines.push('PROJECTS  (positions 1-4 show; ✓ = on the page)');
	projects.forEach((project, i) => {
		lines.push((shown(project) ? ' ✓ ' : ' · ') + (i + 1) + '. ' + project.id + '  |  ' + layoutProjectTitle(project) + score(project));
	});

	const owners = Array.from(document.querySelectorAll('.experience .job')).concat(projects);
	owners.forEach(owner => {
		const label = owner.classList.contains('project') ? layoutProjectTitle(owner) : layoutText(owner.querySelector('.company') || owner);
		if (query ? !label.toLowerCase().includes(query) : !shown(owner)) return;
		lines.push('');
		lines.push('BULLETS: ' + label + (query ? '  (all; ✓ = on the page)' : ''));
		Array.from(owner.querySelectorAll('.highlight')).forEach((bullet, i) => {
			if (!query && !shown(bullet)) return;
			lines.push((shown(bullet) ? ' ✓ ' : ' · ') + (i + 1) + '. ' + clip(layoutText(bullet), 100) + score(bullet));
		});
	});

	const text = lines.join('\n');
	console.log(text);
	return text;
};

window.recipe = function () {
	const quote = value => "'" + String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'";
	const lines = ['tailor(' + window.selectedTags.map(quote).join(', ') + ');'];
	window.layoutOps.forEach(op => {
		if (!op.el.isConnected) return;
		lines.push(op.el.classList.contains('project')
			? 'moveProject(' + quote(op.el.id) + ', ' + op.position + ');'
			: 'moveBullet(' + quote(uniqueFragment(op.el)) + ', ' + op.position + ');');
	});
	if (window.jobBulletCounts.some(count => Number.isInteger(count))) {
		lines.push('jobBullets(' + [0, 1, 2].map(i => (Number.isInteger(window.jobBulletCounts[i]) ? window.jobBulletCounts[i] : 'null')).join(', ') + ');');
	}
	const text = lines.join('\n');
	console.log(text);
	return text;
};

window.resetLayout = function () {
	window.layoutOps = [];
	window.jobBulletCounts = [];
	updateTagResults();
	console.log('resetLayout(): manual moves cleared; order follows the selected tags again.');
};

function resumeTagsInit() {
	document.querySelectorAll('.skill .tag').forEach(tagEl => {
		// Native <button> elements are already focusable and fire 'click' on both
		// Enter and Space, so no manual role/tabindex/keydown handling is needed here.
		tagEl.setAttribute('aria-pressed', 'false');

		tagEl.addEventListener('click', () => {
			toggleTag(tagEl);
			updateTagResults();
		});
	});
}

// ?project=<id> (linked from each portfolio case study): tailor the resume to that
// project's own skill tags, pin the project to the top of Selected Projects (only
// the top few are shown), and scroll to it. Tags that aren't resume skills are
// skipped quietly.
function resumeProjectFromUrl() {
	const id = new URLSearchParams(window.location.search).get('project');
	const project = id ? document.getElementById(id) : null;
	if (!project || !project.classList.contains('project')) return;

	const skills = new Set(Array.from(document.querySelectorAll('.skill .tag')).map(el => el.textContent.trim()));
	window.pinnedProject = id;
	window.tailor(...getOwnTags(project).filter(t => skills.has(t)));
	project.scrollIntoView({ block: 'start' });
}

// ---------------------------------------------------------------------------
// First-visit tour: a few windows, each pointing at part of the page. A step with
// a target spotlights it above a dimmed page (still clickable, so the visitor can
// try a tag mid-tour) and points at it from the side, or from below when there's
// no room beside it. A step without a target sits in the middle of the screen.
// Steps whose target isn't on the page (the form is gone once a message is sent)
// are dropped. window.resumeTour() replays it.
// ---------------------------------------------------------------------------
const TOUR_STEPS = [
	{
		target: '.skill.languages-frameworks',
		title: 'Tailor this resume',
		text: [
			// Below 800px the page is one column (styles/resume.scss).
			() => 'Select the skills that matter for your role from the tags ' +
				(window.matchMedia('(max-width: 799px)').matches ? 'in these skill lists.' : 'in the left column.'),
			'The summary, experience bullets and projects reorder to show the most relevant work first. Select a tag again to remove it.'
		]
	},
	{
		target: '#resume-contact-form',
		title: 'Get in touch',
		text: [
			'For my phone number, or anything else, send me a message here. It comes straight to my inbox.'
		]
	},
	{
		title: 'Print it or save a PDF',
		text: [
			'This page is also a two-page paper resume. Print it from your browser, or choose “Save as PDF” in the print dialog.',
			'Whatever skills you’ve selected carry over to the printout.'
		]
	}
];

const TOUR_GAP = 20; // space between the card and its target, room for the arrow
const TOUR_EDGE = 16; // the card never comes closer than this to the screen edge

const tour = { steps: [], index: 0, card: null, backdrop: null, target: null, returnFocus: null };

function tourReducedMotion() {
	return document.documentElement.dataset.motion === 'reduce' ||
		(!document.documentElement.dataset.motion && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function tourBuild() {
	tour.backdrop = document.createElement('div');
	tour.backdrop.className = 'resume-tour-backdrop';
	tour.backdrop.addEventListener('click', tourClose);

	tour.card = document.createElement('div');
	tour.card.className = 'resume-tour box box-pad';
	tour.card.setAttribute('role', 'dialog');
	tour.card.setAttribute('aria-labelledby', 'resume-tour-title');
	tour.card.setAttribute('aria-describedby', 'resume-tour-text');
	tour.card.innerHTML =
		'<span class="resume-tour-arrow" aria-hidden="true"></span>' +
		'<p class="resume-tour-count"></p>' +
		'<h2 class="resume-tour-title" id="resume-tour-title" tabindex="-1"></h2>' +
		'<div class="resume-tour-text" id="resume-tour-text"></div>' +
		'<p class="button-row">' +
			'<button type="button" class="resume-tour-next primary"></button>' +
			'<button type="button" class="resume-tour-back">Back</button>' +
			'<button type="button" class="resume-tour-close">Skip</button>' +
		'</p>';

	tour.card.querySelector('.resume-tour-next').addEventListener('click', () => {
		if (tour.index < tour.steps.length - 1) tourShow(tour.index + 1);
		else tourClose();
	});
	tour.card.querySelector('.resume-tour-back').addEventListener('click', () => tourShow(tour.index - 1));
	tour.card.querySelector('.resume-tour-close').addEventListener('click', tourClose);
}

// The first step jumps straight to its target: the page has only just loaded,
// and a smooth scroll then is easily cancelled. Later steps glide there, so the
// visitor sees where the page went.
function tourShow(index) {
	const step = tour.steps[index];
	const last = index === tour.steps.length - 1;
	tour.index = index;

	if (tour.target) tour.target.classList.remove('resume-tour-target');
	tour.target = step.target ? document.querySelector(step.target) : null;
	if (tour.target) tour.target.classList.add('resume-tour-target');

	tour.card.querySelector('.resume-tour-count').textContent = (index + 1) + ' of ' + tour.steps.length;
	tour.card.querySelector('.resume-tour-title').textContent = step.title;
	tour.card.querySelector('.resume-tour-text').innerHTML = step.text.map(p => '<p>' + (typeof p === 'function' ? p() : p) + '</p>').join('');
	tour.card.querySelector('.resume-tour-next').textContent = last ? 'Done' : 'Next';
	tour.card.querySelector('.resume-tour-back').hidden = index === 0;
	tour.card.querySelector('.resume-tour-close').hidden = last;

	tourPosition();
	tourScroll(index > 0);
	tour.card.querySelector('.resume-tour-title').focus({ preventScroll: true });
}

// Beside the target if there's room, otherwise below it; centred on screen when
// there's no target. Coordinates are page coordinates, so the card scrolls with
// the target it points at.
function tourPosition() {
	const card = tour.card;
	const viewport = document.documentElement.clientWidth;
	card.classList.remove('beside', 'below', 'centered');
	card.style.top = card.style.left = '';
	card.style.removeProperty('--arrow-offset');

	if (!tour.target) {
		card.classList.add('centered');
		return;
	}

	const rect = tour.target.getBoundingClientRect();
	const width = card.offsetWidth;
	const top = rect.top + window.scrollY;

	if (viewport - rect.right >= width + TOUR_GAP + TOUR_EDGE) {
		card.classList.add('beside');
		card.style.left = (rect.right + window.scrollX + TOUR_GAP) + 'px';
		card.style.top = top + 'px';
		// Level with the target's heading, or its middle if it's short.
		card.style.setProperty('--arrow-offset', Math.min(rect.height / 2, 28) + 'px');
	} else {
		card.classList.add('below');
		const left = Math.max(TOUR_EDGE, Math.min(rect.left, viewport - width - TOUR_EDGE));
		card.style.left = (left + window.scrollX) + 'px';
		card.style.top = (rect.bottom + window.scrollY + TOUR_GAP) + 'px';
		const point = rect.left + Math.min(rect.width / 2, 40);
		card.style.setProperty('--arrow-offset', Math.max(16, point - left) + 'px');
	}
}

// Bring the target and its card into view together, clear of the sticky header.
function tourScroll(smooth) {
	if (!tour.target) return;
	const header = document.querySelector('.site-header');
	const headerHeight = header ? header.offsetHeight : 0;
	const targetRect = tour.target.getBoundingClientRect();
	const cardRect = tour.card.getBoundingClientRect();
	const top = Math.min(targetRect.top, cardRect.top);
	const bottom = Math.max(targetRect.bottom, cardRect.bottom);
	const room = window.innerHeight - headerHeight - 2 * TOUR_EDGE;
	const offset = bottom - top <= room ? top : targetRect.top;
	window.scrollTo({
		top: window.scrollY + offset - headerHeight - TOUR_EDGE,
		behavior: smooth && !tourReducedMotion() ? 'smooth' : 'auto'
	});
}

function tourKeydown(event) {
	if (event.key === 'Escape') tourClose();
}

function tourResize() {
	if (tour.card) tourPosition();
}

// Content can still move once the tour is up (a tag selected mid-tour reorders the
// page), so the card follows its target whenever the page changes size.
const tourObserver = window.ResizeObserver ? new ResizeObserver(tourResize) : null;

function tourClose() {
	if (!tour.card) return;
	if (tour.target) tour.target.classList.remove('resume-tour-target');
	tour.card.remove();
	tour.backdrop.remove();
	tour.card = tour.backdrop = tour.target = null;
	document.removeEventListener('keydown', tourKeydown);
	window.removeEventListener('resize', tourResize);
	if (tourObserver) tourObserver.disconnect();
	if (tour.returnFocus && tour.returnFocus.isConnected) tour.returnFocus.focus({ preventScroll: true });
}

window.resumeTour = function () {
	tourClose();
	tour.steps = TOUR_STEPS.filter(step => !step.target || document.querySelector(step.target));
	if (!tour.steps.length) return;
	tour.returnFocus = document.activeElement;
	tourBuild();
	document.body.append(tour.backdrop, tour.card);
	document.addEventListener('keydown', tourKeydown);
	window.addEventListener('resize', tourResize);
	if (tourObserver) tourObserver.observe(document.querySelector('.resume-main'));
	tourShow(0);
};

// Browsers name a saved PDF after the page title, so while printing the title becomes
// "Eric Savage - Senior Software Developer - Resume" (read from the page heading, so it
// follows resume.json, or a title edited in for one application), then goes back.
function resumePrintTitleInit() {
	const name = document.querySelector('.page-title .name');
	const title = document.querySelector('.page-title .title');
	if (!name || !title) return;
	let siteTitle = document.title;
	window.addEventListener('beforeprint', () => {
		siteTitle = document.title;
		document.title = [layoutText(name), layoutText(title), 'Resume'].join(' - ');
	});
	window.addEventListener('afterprint', () => {
		document.title = siteTitle;
	});
}

// This script tag is the last thing before </body>, so the DOM is already parsed —
// no need to wait for window.onload/afterLoad the way main.js's other page hooks do.
resumeTagsInit();
resumePrintTitleInit();
resumeProjectFromUrl();

// The tour starts on its own only on a first visit: once shown, the "resume_tour"
// cookie (listed in lib/cookies.php) keeps it from coming back, even if it was
// skipped. ?tour=1 shows it regardless. Not over a resume opened from a case
// study's link either: that visitor has already been taken to the project they
// came from.
function resumeTourAuto() {
	const params = new URLSearchParams(window.location.search);
	const seen = /(?:^|;\s*)resume_tour=/.test(document.cookie);
	if (params.has('tour') || (!seen && !params.has('project'))) {
		// Wait for the page and its fonts to settle: the browser's own scrolling
		// during load cancels the tour's scroll to its first step, and late fonts
		// move the target out from under the card.
		const loaded = document.readyState === 'complete'
			? Promise.resolve()
			: new Promise(resolve => window.addEventListener('load', resolve, { once: true }));
		loaded
			.then(() => document.fonts ? document.fonts.ready : null)
			.then(() => window.resumeTour());
		const secure = location.protocol === 'https:' ? '; secure' : '';
		document.cookie = 'resume_tour=seen; path=/; max-age=' + (60 * 60 * 24 * 365) + '; samesite=lax' + secure;
	}
}
resumeTourAuto();
